// ============================================
// FILE: src/components/OrdersScreen.jsx
// ============================================

import React, { useEffect, useState } from 'react';
import { Package, MapPin, Clock, CheckCircle, Truck, XCircle, ChevronLeft, ChevronRight, X, Phone, Mail, Calendar, ArrowLeft, Search } from 'lucide-react';
import { getOrderItemImage } from '../utils/imageHelpers';
import orderHelpers from '../utils/orderHelpers';

const OrdersScreen = ({
  orders = [],
  loading = false,
  loadOrders,
  pagination = null,
  currentPage = 1,
  onPageChange,
  addToCart,
  showToast,
  loadCart,
  setActiveTab,
  goBack,
  highlightedOrderId = null,
  onHighlightedOrderHandled,
  appTheme = 'blue',
  orderSearch = '',
  onSearchChange,
  upiQrCode = '/static/upi-qr.jpeg',
  upiId = '',
  onOrderCancelled,
}) => {
  
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchInput, setSearchInput] = useState(orderSearch);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);


  // Header style for theme consistency with CartScreen
  const headerStyle = appTheme === 'white'
    ? { background: '#fff', color: '#222', boxShadow: '0 2px 16px 0 rgba(0,0,0,0.04)' }
    : { background: 'var(--header-gradient)', color: 'var(--text-light)' };

  // Debug logging to check order data structure
  useEffect(() => {
    console.log('📦 OrdersScreen - Orders data:', orders);
    if (orders && orders.length > 0) {
      console.log('📦 First order address fields:', {
        deliveryAddress: orders[0].deliveryAddress,
        delivery_address: orders[0].delivery_address,
        address: orders[0].address
      });
    }
  }, [orders]);

  useEffect(() => {
    // Load orders when component mounts
    if (loadOrders) {
      loadOrders();
    }
  }, []);

  useEffect(() => {
    if (!highlightedOrderId || !Array.isArray(orders) || orders.length === 0) {
      return;
    }

    const matchedOrder = orders.find((order) => {
      const candidateId = order?.id ?? order?.order_id;
      return String(candidateId) === String(highlightedOrderId);
    });

    if (!matchedOrder) {
      return;
    }

    setSelectedOrder(matchedOrder);
    if (typeof onHighlightedOrderHandled === 'function') {
      onHighlightedOrderHandled();
    }
  }, [highlightedOrderId, onHighlightedOrderHandled, orders]);

  const getItemWeightUnit = (item) => {
    const weight = item.weight ?? item.product?.weight;
    const unit = item.unit ?? item.product?.unit;
    if (!weight && !unit) {
      return '';
    }
    return `${weight ? String(weight) : ''}${unit ? ` ${unit}` : ''}`.trim();
  };

  const handleTrackOrder = async (order) => {
    try {
      const result = await orderHelpers.trackOrder(order.id);
      if (result.success) {
        const { status, location, estimatedDelivery } = result;
        const trackingMessage = `Status: ${status}\nLocation: ${location}\nEstimated Delivery: ${estimatedDelivery}`;
        showToast && showToast(trackingMessage, 'success');
      } else {
        showToast && showToast(result.message || 'Failed to track order', 'error');
      }
    } catch (error) {
      console.error('❌ Track order error:', error);
      showToast && showToast('Failed to track order: ' + error.message, 'error');
    }
  };

  const handleReorder = async (order) => {
    try {
      const result = await orderHelpers.reorder(order.id);
      if (result.success) {
        // Reload cart from backend to reflect the new items
        if (loadCart) {
          await loadCart();
        }
        showToast && showToast('Items added to cart!', 'success');
      } else {
        showToast && showToast(result.message || 'Failed to reorder', 'error');
      }
    } catch (error) {
      showToast && showToast('Failed to reorder', 'error');
    }
  };

  const cancellableStatuses = ['pending', 'confirmed', 'processing'];

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const result = await orderHelpers.cancelOrder(cancelTarget.id, cancelReason);
      if (result.success) {
        showToast && showToast('Order cancelled successfully', 'success');
        setCancelTarget(null);
        setCancelReason('');
        loadOrders && loadOrders();
        onOrderCancelled && onOrderCancelled();
      } else {
        showToast && showToast(result.message || 'Failed to cancel order', 'error');
      }
    } catch (error) {
      showToast && showToast('Failed to cancel order', 'error');
    } finally {
      setCancelling(false);
    }
  };

  // Helper function to format address (handles both string and object)
  const formatAddress = (order) => {
    // Check if deliveryAddress is a string
    if (typeof order.deliveryAddress === 'string' && order.deliveryAddress) {
      return order.deliveryAddress;
    }
    
    // Check if delivery_address is a string
    if (typeof order.delivery_address === 'string' && order.delivery_address) {
      return order.delivery_address;
    }
    
    // Handle address object
    const addressObj = order.deliveryAddress || order.delivery_address || order.address;
    
    if (addressObj && typeof addressObj === 'object') {
      // If there's a full_address field, use it
      if (addressObj.full_address) {
        return addressObj.full_address;
      }
      
      // Otherwise, construct address from parts
      const parts = [
        addressObj.address,
        addressObj.landmark,
        addressObj.city,
        addressObj.state,
        addressObj.pincode
      ].filter(Boolean);
      
      return parts.length > 0 ? parts.join(', ') : 'Address not available';
    }
    
    return 'Address not available';
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'delivered':
        return <CheckCircle size={20} className="text-green-600" />;
      case 'in_transit':
        return <Truck size={20} className="text-blue-600" />;
      case 'cancelled':
        return <XCircle size={20} className="text-red-600" />;
      default:
        return <Package size={20} className="text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'delivered':
        return 'bg-green-100 text-green-700';
      case 'in_transit':
        return 'bg-blue-100 text-blue-700';
      case 'cancelled':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusText = (status) => {
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (loading) {
    return (
      <div className="flex-1 bg-gradient-to-br from-blue-50 to-purple-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your orders...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-20 min-h-screen bg-gray-50">
      <div className="p-6 rounded-b-3xl" style={headerStyle}>
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={() => goBack ? goBack() : setActiveTab('home')}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
            title="Go Back"
            style={appTheme === 'white' ? { color: '#222' } : { color: 'var(--text-light)' }}
          >
            <ArrowLeft size={22} />
          </button>
        </div>
        <h1 className="text-2xl font-bold" style={appTheme === 'white' ? { color: '#222' } : { color: 'var(--text-light)' }}>My Orders</h1>
        <p style={appTheme === 'white' ? { color: '#888' } : { color: 'var(--accent-light)' }}>Track your dairy deliveries</p>
      </div>

      <div className="px-4 pt-4 pb-2">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && onSearchChange && onSearchChange(searchInput)}
              placeholder="Search by order number, name or phone..."
              className="w-full pl-3 pr-9 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-blue-400 shadow-sm"
            />
            {searchInput ? (
              <button
                onClick={() => { setSearchInput(''); onSearchChange && onSearchChange(''); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            ) : null}
          </div>
          <button
            onClick={() => onSearchChange && onSearchChange(searchInput)}
            className="px-4 py-2.5 rounded-xl bg-blue-500 text-white text-sm font-medium flex items-center gap-1.5 shadow-sm hover:bg-blue-600 active:scale-95 transition-all"
          >
            <Search size={16} />
            Search
          </button>
        </div>
      </div>

      <div className="px-4 pb-4">
        {orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center mt-16 px-6 text-center">
            <Package size={64} className="text-gray-300 mb-4" />
            {orderSearch ? (
              <>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No orders found</h3>
                <p className="text-gray-400">No results for "<span className="font-medium text-gray-500">{orderSearch}</span>". Try a different search.</p>
                <button
                  onClick={() => { setSearchInput(''); onSearchChange && onSearchChange(''); }}
                  className="mt-4 px-4 py-2 rounded-xl bg-blue-500 text-white text-sm font-medium hover:bg-blue-600 transition-all"
                >
                  Clear Search
                </button>
              </>
            ) : (
              <>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No orders yet</h3>
                <p className="text-gray-400">Start shopping to see your orders here!</p>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map(order => (
              <div key={order.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
                {/* Order Header */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 border-b">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-bold text-gray-800">
                        Order #{order.order_number || order.id}
                      </h3>
                      <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                        <Clock size={14} />
                        {order.date || new Date(order.created_at || order.order_date).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(order.status)}`}>
                      {getStatusIcon(order.status)}
                      {getStatusText(order.status)}
                    </span>
                  </div>
                  
                  {(order.estimatedDelivery || order.estimated_delivery) && (
                    <div className="bg-blue-100 border border-blue-200 rounded-lg p-2 mt-2">
                      <p className="text-xs text-blue-800 font-medium">
                        🚚 Estimated Delivery: {order.estimatedDelivery || order.estimated_delivery}
                      </p>
                    </div>
                  )}
                </div>

                {/* Order Items */}
                <div className="p-4">
                  <div className="space-y-3 mb-3">
                    {(order.items || order.order_items || []).map((item, index) => (
                      <div key={index} className="flex items-center gap-3 justify-between text-sm">
                        <div className="flex items-center gap-3 flex-1">
                          {getOrderItemImage(item, 'small')}
                          <div className="flex-1">
                            <span className="text-gray-700 font-medium">
                              {item.name || item.product_name || item.product?.name}
                            </span>
                            {getItemWeightUnit(item) && (
                              <div className="text-xs text-gray-500 mt-1">
                                {getItemWeightUnit(item)}
                              </div>
                            )}
                            <div className="text-xs text-gray-500 mt-1">
                              Qty: {item.quantity}
                              {(item.sale_price || item.product?.sale_price) && (item.sale_price || item.product?.sale_price) < (item.price || item.product_price || item.product?.price) && (
                                <span className="ml-2">
                                  <span className="text-red-600 font-semibold">₹{item.sale_price || item.product?.sale_price}</span>
                                  <span className="text-gray-400 line-through ml-1">₹{item.price || item.product_price || item.product?.price}</span>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <span className="text-gray-600 font-semibold">
                          ₹{(Number((item.sale_price && item.sale_price < item.price ? item.sale_price : null) || item.price || item.product_price || item.product?.price || 0) * Number(item.quantity)).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="border-t pt-3 mb-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="text-gray-600">
                        ₹{Number(order.original_amount || order.originalAmount || order.subtotal || 0).toFixed(2)}
                      </span>
                    </div>
                    {order.coupon_discount && order.coupon_discount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-green-600">
                          Discount {order.coupon_code ? `(${order.coupon_code})` : ''}
                        </span>
                        <span className="text-green-600">
                          -₹{Number(order.coupon_discount || order.couponDiscount || 0).toFixed(2)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Shipping</span>
                      <span className="text-gray-600">
                        {order.shipping_charge || order.shippingCharge ? (
                          `₹${Number(order.shipping_charge || order.shippingCharge || 0).toFixed(2)}`
                        ) : (
                          <span className="text-green-600">Free</span>
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Tax (GST)</span>
                      <span className="text-gray-600">
                        ₹{Number(order.tax_amount || order.taxAmount || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between font-semibold">
                      <span className="text-gray-800">Total</span>
                      <span className="text-blue-600">
                        ₹{Number(order.total || order.total_amount || order.grand_total || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Delivery Details */}
                  <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg p-4 mb-3 border border-gray-200">
                    <p className="text-xs text-gray-600 mb-3 font-semibold uppercase tracking-wide">Delivery Details</p>
                    
                    {/* Delivery Address */}
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                        <MapPin size={12} className="text-gray-400" />
                        Delivery Address
                      </p>
                      <p className="text-sm text-gray-800 font-medium pl-4">
                        {formatAddress(order)}
                      </p>
                    </div>

                    {/* Delivery Slot */}
                    <div className="mb-3 bg-white rounded-lg p-2 border border-blue-200">
                      <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                        <Clock size={12} className="text-blue-500" />
                        Delivery Time Slot
                      </p>
                      <p className="text-sm text-blue-700 font-semibold">
                        {order.deliverySlot || order.delivery_slot || 'Not scheduled'}
                      </p>
                    </div>

                    {/* Tracking Number */}
                    {(order.trackingNumber || order.tracking_number) && (
                      <div className="bg-white rounded-lg p-2 border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Tracking Number</p>
                        <p className="text-xs text-gray-700 font-mono font-semibold">
                          {order.trackingNumber || order.tracking_number}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    {order.status === 'in_transit' && (
                      <button
                        onClick={() => handleTrackOrder(order)}
                        className="flex-1 bg-blue-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-all"
                      >
                        Track Order
                      </button>
                    )}
                    {order.status === 'delivered' && (
                      <>
                        <button
                          onClick={() => handleReorder(order)}
                          className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-all"
                        >
                          Reorder
                        </button>
                        <button className="flex-1 bg-blue-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-all">
                          Rate Order
                        </button>
                      </>
                    )}
                    {cancellableStatuses.includes(order.status) && (
                      <button
                        onClick={() => { setCancelTarget(order); setCancelReason(''); }}
                        className="flex-1 bg-red-50 text-red-600 py-2 rounded-lg text-sm font-medium hover:bg-red-100 transition-all border border-red-200"
                      >
                        Cancel Order
                      </button>
                    )}
                    <button
                      onClick={() => setSelectedOrder(order)}
                      className="px-4 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-all"
                    >
                      Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination Controls */}
        {pagination && pagination.total_pages > 1 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mt-6">
            <div className="flex items-center justify-between">
              {/* Pagination Info */}
              <div className="text-sm text-gray-600">
                Showing {pagination.from || 1} to {pagination.to || orders.length} of {pagination.total || orders.length} orders
              </div>

              {/* Pagination Buttons */}
              <div className="flex items-center gap-2">
                {/* Previous Button */}
                <button
                  onClick={() => onPageChange && onPageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg transition-all ${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                  }`}
                >
                  <ChevronLeft size={20} />
                </button>

                {/* Page Numbers */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map(page => {
                    // Show first page, last page, current page, and pages around current
                    const showPage = 
                      page === 1 || 
                      page === pagination.total_pages || 
                      (page >= currentPage - 1 && page <= currentPage + 1);
                    
                    // Show ellipsis
                    const showEllipsisBefore = page === currentPage - 2 && currentPage > 3;
                    const showEllipsisAfter = page === currentPage + 2 && currentPage < pagination.total_pages - 2;

                    if (showEllipsisBefore || showEllipsisAfter) {
                      return (
                        <span key={page} className="px-2 text-gray-400">
                          ...
                        </span>
                      );
                    }

                    if (!showPage) return null;

                    return (
                      <button
                        key={page}
                        onClick={() => onPageChange && onPageChange(page)}
                        className={`min-w-[40px] h-10 px-3 rounded-lg text-sm font-medium transition-all ${
                          page === currentPage
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>

                {/* Next Button */}
                <button
                  onClick={() => onPageChange && onPageChange(currentPage + 1)}
                  disabled={currentPage === pagination.total_pages}
                  className={`p-2 rounded-lg transition-all ${
                    currentPage === pagination.total_pages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                  }`}
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Help Section */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-6">
          <h4 className="font-semibold text-gray-800 text-sm mb-2">Need Help?</h4>
          <p className="text-xs text-gray-600 mb-3">
            Having issues with your order? Contact our support team.
          </p>
          <button
            onClick={() => setActiveTab && setActiveTab('help')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-all"
          >
            Contact Support
          </button>
        </div>
      </div>

      {/* Cancel Order Confirmation Modal */}
      {cancelTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-[220]">
          <div className="bg-white rounded-t-3xl w-full max-w-lg p-6 pb-28">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Cancel Order</h3>
              <button
                onClick={() => { setCancelTarget(null); setCancelReason(''); }}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
              >
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-1">
              Order <span className="font-semibold text-gray-700">#{cancelTarget.order_number || cancelTarget.id}</span>
            </p>
            <p className="text-sm text-gray-500 mb-4">This action cannot be undone. Stock will be restored.</p>
            <textarea
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              placeholder="Reason for cancellation (optional)"
              rows={3}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-red-300 resize-none mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => { setCancelTarget(null); setCancelReason(''); }}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-all"
              >
                Keep Order
              </button>
              <button
                onClick={handleCancelConfirm}
                disabled={cancelling}
                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-all disabled:opacity-60"
              >
                {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold mb-1">Order Details</h2>
                <p className="text-blue-100 text-sm">Order #{selectedOrder.order_number || selectedOrder.id}</p>
              </div>
              <button 
                onClick={() => setSelectedOrder(null)}
                className="p-2 hover:bg-white/20 rounded-lg transition-all"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Order Status */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Status</p>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(selectedOrder.status)}
                      <span className="font-semibold text-lg">
                        {getStatusText(selectedOrder.status)}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600 mb-1">Order Date</p>
                    <div className="flex items-center gap-2 justify-end">
                      <Calendar size={16} className="text-gray-500" />
                      <span className="font-medium">
                        {selectedOrder.date || new Date(selectedOrder.created_at || selectedOrder.order_date).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </span>
                    </div>
                  </div>
                </div>
                {(selectedOrder.estimatedDelivery || selectedOrder.estimated_delivery) && (
                  <div className="mt-3 pt-3 border-t border-blue-200">
                    <p className="text-sm text-blue-800 font-medium flex items-center gap-2">
                      <Truck size={16} />
                      Estimated Delivery: {selectedOrder.estimatedDelivery || selectedOrder.estimated_delivery}
                    </p>
                  </div>
                )}
              </div>

              {/* Order Items */}
              <div>
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Package size={18} />
                  Order Items
                </h3>
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  {(selectedOrder.items || selectedOrder.order_items || []).map((item, index) => (
                    <div key={index} className="flex items-center gap-3 pb-3 border-b border-gray-200 last:border-0 last:pb-0">
                      {getOrderItemImage(item, 'medium')}
                      <div className="flex-1">
                        <p className="font-medium text-gray-800">
                          {item.name || item.product_name || item.product?.name}
                        </p>
                        {getItemWeightUnit(item) && (
                          <p className="text-xs text-gray-500 mt-1">
                            {getItemWeightUnit(item)}
                          </p>
                        )}
                        <p className="text-sm text-gray-500 mt-1">
                          Quantity: {item.quantity} × 
                          {(item.sale_price || item.product?.sale_price) && (item.sale_price || item.product?.sale_price) < (item.price || item.product_price || item.product?.price) ? (
                            <>
                              <span className="text-red-600 font-semibold">₹{Number(item.sale_price || item.product?.sale_price).toFixed(2)}</span>
                              <span className="text-gray-400 line-through ml-1">₹{Number(item.price || item.product_price || item.product?.price).toFixed(2)}</span>
                            </>
                          ) : (
                            <span>₹{Number(item.price || item.product_price || item.product?.price || 0).toFixed(2)}</span>
                          )}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-800">
                          ₹{(Number((item.sale_price && item.sale_price < item.price ? item.sale_price : null) || item.price || item.product_price || item.product?.price || 0) * Number(item.quantity)).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                  
                  {/* Total */}
                  <div className="pt-3 mt-3 border-t-2 border-gray-300 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="text-gray-600">
                        ₹{Number(selectedOrder.original_amount || selectedOrder.originalAmount || selectedOrder.subtotal || 0).toFixed(2)}
                      </span>
                    </div>
                    {selectedOrder.coupon_discount && selectedOrder.coupon_discount > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-green-600 text-sm">
                          Discount {selectedOrder.coupon_code ? `(${selectedOrder.coupon_code})` : ''}
                        </span>
                        <span className="text-green-600 font-medium">
                          -₹{Number(selectedOrder.coupon_discount || selectedOrder.couponDiscount || 0).toFixed(2)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Shipping</span>
                      <span className="text-gray-600">
                        {selectedOrder.shipping_charge || selectedOrder.shippingCharge ? (
                          `₹${Number(selectedOrder.shipping_charge || selectedOrder.shippingCharge || 0).toFixed(2)}`
                        ) : (
                          <span className="text-green-600">Free</span>
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Tax (GST)</span>
                      <span className="text-gray-600">
                        ₹{Number(selectedOrder.tax_amount || selectedOrder.taxAmount || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-gray-800 text-lg">Total Amount</span>
                      <span className="font-bold text-blue-600 text-xl">
                        ₹{Number(selectedOrder.total || selectedOrder.total_amount || selectedOrder.grand_total || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Delivery Information */}
              <div>
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <MapPin size={18} />
                  Delivery Information
                </h3>
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-4 space-y-3 border border-blue-200">
                  <div>
                    <p className="text-xs text-gray-500 font-medium mb-1">Delivery Address</p>
                    <p className="text-sm text-gray-800">
                      {formatAddress(selectedOrder)}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 font-medium mb-1">Delivery Slot</p>
                      <p className="text-sm text-gray-800 flex items-center gap-1">
                        <Clock size={14} />
                        {selectedOrder.deliverySlot || selectedOrder.delivery_slot || 'N/A'}
                      </p>
                    </div>
                    {(selectedOrder.trackingNumber || selectedOrder.tracking_number) && (
                      <div>
                        <p className="text-xs text-gray-500 font-medium mb-1">Tracking Number</p>
                        <p className="text-sm text-gray-800 font-mono">
                          {selectedOrder.trackingNumber || selectedOrder.tracking_number}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Payment Information */}
              <div>
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <CheckCircle size={18} />
                  Payment Information
                </h3>
                <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-xs text-gray-500 font-medium mb-1">Payment Method</p>
                      <p className="text-sm text-gray-800 font-medium uppercase">
                        {selectedOrder.paymentMethod || selectedOrder.payment_method || 'COD'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500 font-medium mb-1">Payment Status</p>
                      <span className="inline-block px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        {selectedOrder.payment_status || 'Completed'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* UPI QR Code for UPI Scan on Delivery */}
              {['upi_cod', 'UPI_SCAN_ON_DELIVERY', 'upi-scan-on-delivery'].includes((selectedOrder.paymentMethod || selectedOrder.payment_method || '').toLowerCase()) && (
                <div className="my-6 flex flex-col items-center w-full">
                  <div className="w-full flex justify-center">
                    <div
                      className="bg-white p-4 rounded-2xl shadow-lg border-2 border-gray-200 flex justify-center"
                      style={{ maxWidth: 400, width: '100%' }}
                    >
                      <img
                        src={upiQrCode}
                        alt="UPI QR Code"
                        style={{ width: 240, height: 240, display: 'block', borderRadius: 12 }}
                      />
                    </div>
                  </div>
                  {upiId && (
                    <p className="text-sm font-semibold text-gray-800 mt-2 text-center">{upiId}</p>
                  )}
                  <p className="text-sm text-gray-700 mt-1 text-center font-semibold tracking-wide">Scan this QR at delivery to pay</p>
                </div>
              )}

              {/* Contact Support */}
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <p className="text-sm text-gray-700 mb-3">
                  Need help with this order?
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setSelectedOrder(null);
                      setActiveTab && setActiveTab('help');
                    }}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
                  >
                    <Phone size={16} />
                    Call Support
                  </button>
                  <button
                    onClick={() => {
                      setSelectedOrder(null);
                      setActiveTab && setActiveTab('help');
                    }}
                    className="flex-1 bg-white border border-blue-300 text-blue-600 py-2 px-4 rounded-lg text-sm font-medium hover:bg-blue-50 transition-all flex items-center justify-center gap-2"
                  >
                    <Mail size={16} />
                    Email Us
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersScreen;